import React, { useState, useEffect } from 'react';

const industries = [
  { id: 'trades', name: "Trades & Home Services", rate: 0.27 },
  { id: 'dentists', name: "Dental & Medical Practices", rate: 0.25 },
  { id: 'legal', name: "Legal & Law Firms", rate: 0.35 },
  { id: 'realestate', name: "Real Estate Agencies", rate: 0.40 },
  { id: 'medspa', name: "Salons & MedSpas", rate: 0.35 },
  { id: 'general', name: "General Local Service", rate: 0.25 }
];

const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
}).format(value);

const Calculator = ({ industry: industryId }) => {
  const [weeklyCalls, setWeeklyCalls] = useState(50);
  const [avgValue, setAvgValue] = useState(500);
  const [industryRate, setIndustryRate] = useState(0.25);
  const [results, setResults] = useState({ lostRevenue: 0, lostCustomers: 0, roi: 0 });
  
  const investment = 9387; // $699 * 12 + $999

  // Initialize industry rate based on the passed industry ID
  useEffect(() => {
    if (industryId) {
      const selected = industries.find(ind => ind.id === industryId);
      if (selected) {
        setIndustryRate(selected.rate);
      }
    }
  }, [industryId]);

  useEffect(() => {
    const annualMissed = weeklyCalls * 52 * industryRate;
    const lostCustomers = annualMissed * 0.41 * 0.85; 
    const lostRevenue = lostCustomers * avgValue;
    const roi = lostRevenue - investment;

    setResults({
      lostRevenue,
      lostCustomers: Math.round(lostCustomers),
      roi
    });
  }, [weeklyCalls, avgValue, industryRate]);

  return (
    <section id="calculator" className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Interactive Lost Revenue Calculator</h2>
        <p className="text-slate-400 max-w-2xl mx-auto">See exactly how much revenue is slipping through the cracks and your projected ROI with AutoReception.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-start">
        {/* Inputs */}
        <div className="bg-slate-800 border border-slate-700 rounded-3xl p-8 shadow-xl">
          <div className="space-y-8">
            <div>
              <label className="flex justify-between text-sm font-medium text-slate-300 mb-4">
                <span>Estimated Weekly Inbound Calls</span>
                <span className="text-white font-bold">{weeklyCalls} calls</span>
              </label>
              <input 
                type="range" 
                min="10" 
                max="500" 
                value={weeklyCalls}
                onChange={(e) => setWeeklyCalls(Number(e.target.value))}
                className="w-full accent-blue-500"
              />
            </div>

            <div>
              <label className="flex justify-between text-sm font-medium text-slate-300 mb-4">
                <span>Average Customer Lifetime Value ($)</span>
                <span className="text-white font-bold">{formatCurrency(avgValue)}</span>
              </label>
              <input 
                type="range" 
                min="50" 
                max="10000" 
                step="50"
                value={avgValue}
                onChange={(e) => setAvgValue(Number(e.target.value))}
                className="w-full accent-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Select Your Industry</label>
              <select 
                value={industryRate}
                onChange={(e) => setIndustryRate(Number(e.target.value))}
                className="w-full bg-slate-900 border border-slate-700 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none"
              >
                {industries.map(ind => (
                  <option key={ind.id} value={ind.rate}>{ind.name} (Miss rate: {ind.rate * 100}%)</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Outputs */}
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none"></div>
          
          <div className="space-y-6 relative z-10">
            <div className="pb-6 border-b border-slate-800">
              <p className="text-slate-400 text-sm font-medium mb-1">Estimated Annual Revenue Lost</p>
              <p className="text-5xl font-extrabold text-red-400">{formatCurrency(results.lostRevenue)}</p>
            </div>

            <div className="flex justify-between items-center pb-6 border-b border-slate-800">
              <p className="text-slate-400">Lost Customers per Year</p>
              <p className="text-2xl font-bold text-white">{results.lostCustomers}</p>
            </div>

            <div className="flex justify-between items-center pb-6 border-b border-slate-800">
              <p className="text-slate-400">AutoReception Investment (Year 1)</p>
              <p className="text-xl font-bold text-slate-300">{formatCurrency(investment)}</p>
            </div>

            <div className="pt-2 pb-6">
              <p className="text-emerald-400/80 text-sm font-medium mb-1">Net Projected Recovered ROI</p>
              <p className="text-5xl font-extrabold text-emerald-400">{formatCurrency(results.roi)}</p>
            </div>

            <a href="#contact" className="block w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-center px-6 py-4 rounded-xl font-bold text-lg transition-all shadow-[0_0_20px_rgba(16,185,129,0.3)]">
              Claim Your 3-Day Free Trial
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Calculator;